import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

import {
  getProformaInvoices,
  deleteProformaInvoice,
  getSettings
} from "../services/firestore";

import { generateProformaInvoicePDF } from "../services/pdfGenerator";

import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "../components/ui/alert-dialog";

import {
  Plus,
  Search,
  FileText,
  Building2,
  MoreVertical,
  Pencil,
  Trash2,
  Download,
  ArrowRightLeft
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "../components/ui/dropdown-menu";

import { toast } from "sonner";

export const ProformaInvoicesPage = () => {

  const { userData } = useAuth();
  const navigate = useNavigate();

  const [pis, setPIs] = useState([]);
  const [filteredPIs, setFilteredPIs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [dateFilter, setDateFilter] = useState("all");
  const [customDate, setCustomDate] = useState("");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [piToDelete, setPiToDelete] = useState(null);

  const [settings, setSettings] = useState({});

  /* =============================
     LOAD DATA
  ==============================*/

  useEffect(() => {
    if (userData) fetchData();
  }, [userData]);

  useEffect(() => {
    filterPIs();
  }, [searchQuery, statusFilter, dateFilter, customDate, pis]);

  const fetchData = async () => {
    setLoading(true);

    try {
      const [piData, settingsData] = await Promise.all([
        getProformaInvoices(),
        getSettings()
      ]);

      setPIs(piData);
      setFilteredPIs(piData);
      setSettings(settingsData);

    } catch (error) {
      console.error(error);
      toast.error("Failed to load Proforma Invoices");
    } finally {
      setLoading(false);
    }
  };

  /* =============================
     FILTER
  ==============================*/

  const filterPIs = () => {

    let result = [...pis];

    // SEARCH
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();

      result = result.filter(pi =>
        pi.pi_no?.toLowerCase().includes(q) ||
        pi.party_snapshot?.name?.toLowerCase().includes(q)
      );
    }

    // STATUS
    if (statusFilter !== "all") {
      result = result.filter(pi => (pi.status || "draft") === statusFilter);
    }

    // DATE FILTER
    if (dateFilter !== "all") {

      const now = new Date();

      result = result.filter(pi => {

        const dRaw = pi.created_at?.toDate?.() || pi.created_at;
        if (!dRaw) return false;

        const d = new Date(dRaw);
        if (isNaN(d)) return false;

        if (dateFilter === "last7") {
          const past = new Date();
          past.setDate(now.getDate() - 7);
          return d >= past;
        }

        if (dateFilter === "last30") {
          const past = new Date();
          past.setDate(now.getDate() - 30);
          return d >= past;
        }

        if (dateFilter === "custom") {
          if (!customDate) return false;

          const selected = new Date(customDate);
          selected.setHours(0, 0, 0, 0);

          const dDate = new Date(d);
          dDate.setHours(0, 0, 0, 0);

          return dDate >= selected;
        }

        return true;

      });
    }

    setFilteredPIs(result);
  };

  /* =============================
     DELETE
  ==============================*/

  const handleDelete = async () => {
    if (!piToDelete) return;

    try {
      await deleteProformaInvoice(piToDelete.id);
      toast.success("Proforma Invoice deleted");
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete Proforma Invoice");
    } finally {
      setDeleteDialogOpen(false);
      setPiToDelete(null);
    }
  };

  /* =============================
     PDF
  ==============================*/

  const handleDownloadPDF = (pi) => {
    try {
      generateProformaInvoicePDF(
        pi,
        settings,
        userData?.name || "user"
      );
      toast.success("PDF downloaded");
    } catch (error) {
      console.error(error);
      toast.error("PDF generation failed");
    }
  };

  /* =============================
     CONVERT
  ==============================*/

  const handleConvertToSOA = (pi) => {
    navigate(`/soa/new?fromPI=${pi.id}`);
  };

  /* =============================
     HELPERS
  ==============================*/

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const getStatusBadge = (status) => {
    const finalStatus = status || "draft";

    const map = {
      draft: { label: "Draft", className: "badge-draft" },
      submitted: { label: "Submitted", className: "badge-submitted" },
      payment_received: { label: "Payment Received", className: "badge-approved" },
      lost: { label: "Lost", className: "badge-lost" },
      in_process: { label: "In Process", className: "badge-inprocess" }
    };

    const cfg = map[finalStatus] || map.draft;

    return <Badge className={cfg.className}>{cfg.label}</Badge>;
  };

  /* =============================
     CARD
  ==============================*/

  const PICard = ({ pi }) => (
    <Card className="data-card">
      <CardContent className="p-4">

        <div className="flex justify-between">

          <div className="flex gap-3 flex-1">

            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>

            <div className="flex-1">

              <div className="flex gap-2 flex-wrap">
                <span className="font-mono text-sm font-semibold">
                  {pi.pi_no}
                </span>

                {getStatusBadge(pi.status)}
              </div>

              <p className="text-sm text-slate-500 flex gap-1 mt-1">
                <Building2 size={14} />
                {pi.party_snapshot?.name || "No party"}
              </p>

              <p className="text-xs text-slate-400 mt-1">
                {formatDate(pi.created_at)}
              </p>

            </div>

          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical size={16} />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">

              <DropdownMenuItem onClick={() => navigate(`/proforma-invoices/${pi.id}`)}>
                <FileText size={14} className="mr-2" /> View
              </DropdownMenuItem>

              <DropdownMenuItem onClick={() => navigate(`/proforma-invoices/${pi.id}/edit`)}>
                <Pencil size={14} className="mr-2" /> Edit
              </DropdownMenuItem>

              <DropdownMenuItem onClick={() => handleDownloadPDF(pi)}>
                <Download size={14} className="mr-2" /> Download PDF
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={() => handleConvertToSOA(pi)}>
                <ArrowRightLeft size={14} className="mr-2" /> Convert to SOA
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                className="text-red-600"
                onClick={() => {
                  setPiToDelete(pi);
                  setDeleteDialogOpen(true);
                }}
              >
                <Trash2 size={14} className="mr-2" /> Delete
              </DropdownMenuItem>

            </DropdownMenuContent>
          </DropdownMenu>

        </div>

        <div className="flex justify-between mt-3 pt-3 border-t">
          <span className="text-xs text-slate-500">
            {pi.items?.length || 0} item(s)
          </span>

          <span className="text-lg font-semibold">
            {formatCurrency(pi.totals?.grand_total)}
          </span>
        </div>

      </CardContent>
    </Card>
  );

  /* =============================
     LOADING
  ==============================*/

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  /* =============================
     PAGE
  ==============================*/

  return (
    <div className="p-6 space-y-4">

      <div className="flex justify-between items-center">

        <div>
          <h1 className="text-2xl font-bold">Proforma Invoices</h1>
          <p className="text-sm text-slate-500">
            {filteredPIs.length} of {pis.length} documents
          </p>
        </div>

        <Button onClick={() => navigate("/proforma-invoices/new")}>
          <Plus className="w-4 h-4 mr-2" />
          New PI
        </Button>

      </div>

      {/* FILTER BAR */}

      <div className="flex gap-3 flex-wrap">

        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border rounded px-3 h-12"
        >
          <option value="all">All</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="payment_received">Payment Received</option>
          <option value="lost">Lost</option>
          <option value="in_process">In Process</option>
        </select>

        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="border rounded px-3 h-12"
        >
          <option value="all">All Time</option>
          <option value="last7">Last 7 Days</option>
          <option value="last30">Last 30 Days</option>
          <option value="custom">Custom Date</option>
        </select>

        {dateFilter === "custom" && (
          <input
            type="date"
            value={customDate}
            onChange={(e) => setCustomDate(e.target.value)}
            className="border rounded px-3 h-12"
          />
        )}

        <Button
          variant="outline"
          onClick={() => {
            setSearchQuery("");
            setStatusFilter("all");
            setDateFilter("all");
            setCustomDate("");
          }}
        >
          Clear
        </Button>

      </div>

      {/* LIST */}

      {filteredPIs.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">No Proforma Invoices found</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredPIs.map(pi => (
            <PICard key={pi.id} pi={pi} />
          ))}
        </div>
      )}

      {/* DELETE DIALOG */}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Proforma Invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{piToDelete?.pi_no}" ?
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-500" onClick={handleDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default ProformaInvoicesPage;